import { useState, useEffect, useRef } from 'react';
import { formatDateToMDY } from '../../../constants/utils';
import {
  EXPIRY_DATE,
  LAST_INSTALLATION_DATE,
  LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
} from '../constants';
import {
  getExpiryDate,
  getOfferId,
  isValidRestrictedStores,
} from '../service/CreatePromotionService';

export const useFinancialOffer = ({
  index,
  offersList = [],
  defaultExpiryDate,
  promotionData,
  setPromotionData,
  mode,
  selectedStores = [],
  loadedPromotionData,
  historicPromotionData,
  setSelectedConjunction,
  fetchHeadlineData,
}) => {
  const [showDatePickerForFinanceOfferID, setShowDatePickerForFinanceOfferID] = useState(1);
  const [offerApplicable, setOfferApplicable] = useState(false);
  const [configureHeadline, setConfigureHeadline] = useState(false);
  const [headlineOfferValues, setHeadlineOfferValues] = useState({});
  const [selectVendor, setSelectVendor] = useState(null);
  const [selectHeadline, setSelectHeadline] = useState(null);
  const [disableConfig, setDisableConfig] = useState(true);
  const [variableValues, setVariableValues] = useState({});
  const [expiryDatesObj, setExpiryDatesObj] = useState({});
  const [customHeadline, setCustomHeadline] = useState('');
  const [customSubHeadline, setCustomSubHeadline] = useState('');
  const [customDisclaimer, setCustomDisclaimer] = useState('');
  const [planID, setPlanID] = useState('');
  const [promoExpiry, setPromoExpiry] = useState({});
  const [valuesForD1, setValuesForD1] = useState([]);
  const [valuesForD2, setValuesForD2] = useState([]);
  const [valuesForD3, setValuesForD3] = useState([]);
  const [modifiedOfferData, setModifiedOfferData] = useState([]);
  const [vendorsListOptions, setVendorsListOptions] = useState([]);
  const [offers, setOffers] = useState([]);
  const valuesLoadedInitially = useRef(mode !== 'Edit');
  const [offerIdError, setOfferIdError] = useState(false);
  const initialHomeConsulSchedExpDateRef = useRef(null);

  // Initialize vendor and offer options
  useEffect(() => {
    const uniqueVendors = new Set();
    const allOptions = {
      label: 'Show All Vendor Offers',
      value: 'all_vendor',
      dropdown_value_label: '',
    };

    const customOffer = {
      label: 'Custom Offer (Click Configure button to set value)',
      value: 'custom_offer',
    };

    const noOffer = {
      label: 'None',
      value: 'none',
      id: 'none-headline',
    };

    const offers = offersList.reduce((accumulator, eachOffer) => {
      const { vendor, restricted_stores } = eachOffer;

      if (vendor && !uniqueVendors.has(vendor)) {
        if (isValidRestrictedStores(restricted_stores, selectedStores)) {
          uniqueVendors.add(vendor);
          accumulator.push({
            label: vendor,
            value: vendor,
          });
        }
      }
      return accumulator;
    }, []);

    const filteredOffers = offersList.filter((eachOffer) =>
      isValidRestrictedStores(eachOffer.restricted_stores, selectedStores)
    );

    const allOffers = filteredOffers.map((eachOffer) => ({
      label: `${eachOffer.template}`,
      value: eachOffer.id,
      ...eachOffer,
    }));

    setModifiedOfferData([noOffer, ...allOffers, customOffer]);
    setOffers([noOffer, ...allOffers, customOffer]);
    setVendorsListOptions([allOptions, ...offers]);
  }, [offersList, selectedStores]);

  // Helper function to determine headline value
  const getHeadlineValue = (financeOffer) => {
    if (financeOffer.headline_builder_id) {
      return financeOffer.headline_builder_id;
    }
    if (financeOffer.custom) {
      return 'custom_offer';
    }
    return 'none';
  };

  // Helper function to determine if config should be disabled
  const shouldDisableConfig = (financeOffer) => {
    const matchedOffer = offersList.find((offer) => offer.id === financeOffer.headline_builder_id);
    const hasTemplateVariables = matchedOffer?.template_variables?.length > 0;
    return !hasTemplateVariables && financeOffer.custom !== true;
  };

  // Helper function to handle expiry type settings
  const applyExpiryTypeSettings = (expiryType, expiryDates) => {
    if (expiryType === EXPIRY_DATE) {
      setPromoExpiry({ EXPIRY_DATE: expiryDates });
      setShowDatePickerForFinanceOfferID(1);
      return;
    }

    if (expiryType === LAST_INSTALLATION_DATE) {
      setPromoExpiry({ LAST_INSTALLATION_DATE: expiryDates });
      setShowDatePickerForFinanceOfferID(2);
      return;
    }

    if (expiryType === LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE) {
      setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: expiryDates });
      setShowDatePickerForFinanceOfferID(3);
    }
  };

  // Helper function to load finance offer data
  const loadFinanceOfferData = (financeOffer) => {
    setOfferApplicable(true);
    setSelectHeadline(getHeadlineValue(financeOffer));
    setCustomHeadline(financeOffer.headline || '');
    setCustomSubHeadline(financeOffer.sub_headline || '');
    setCustomDisclaimer(financeOffer.disclaimer || '');
    setExpiryDatesObj(
      financeOffer.offer_expiry !== undefined ? getExpiryDate(financeOffer.offer_expiry) : []
    );
    setVariableValues(financeOffer.custom_values || {});
    setSelectVendor(financeOffer.vendor || '');
    setDisableConfig(shouldDisableConfig(financeOffer));

    const offerIdToFind =
      financeOffer?.offer_id || historicPromotionData?.finance_offer?.[index]?.offer_id;

    if (offerIdToFind !== undefined) {
      setPlanID(offerIdToFind);
      debouncedFindOffer(offerIdToFind);
    }

    if (financeOffer?.offer_expiry?.offer_expiry_type) {
      applyExpiryTypeSettings(
        financeOffer.offer_expiry.offer_expiry_type,
        financeOffer.offer_expiry.expiry_dates
      );
    }
  };

  // Load initial values for edit mode
  useEffect(() => {
    const shouldSkipLoading =
      !promotionData ||
      !loadedPromotionData ||
      Object.keys(promotionData).length === 0 ||
      valuesLoadedInitially.current ||
      index === undefined ||
      offersList.length === 0 ||
      offers.length === 0;

    if (shouldSkipLoading) {
      return;
    }

    const financeOffers = promotionData?.finance_offer;
    const financeOffer = financeOffers ? financeOffers[index] : {};
    const hasFinanceOffer = financeOffer !== undefined && Object.keys(financeOffer).length !== 0;

    if (hasFinanceOffer) {
      loadFinanceOfferData(financeOffer);
    }

    valuesLoadedInitially.current = true;
  }, [promotionData, loadedPromotionData, offers, index, offersList, historicPromotionData]);

  const findOfferById = (offerId) => {
    for (let index = 0; index < offersList.length; index++) {
      const entry = offersList[index];

      if (entry.options && entry.options.length > 0) {
        const foundOffer = entry.options.find((option) => option.offer_id === offerId);

        if (foundOffer) {
          return { foundOffer, index };
        }
      }
    }
    return { foundOffer: null, index: -1 };
  };

  const updateDropdownValues = (value) => {
    if (value?.length) {
      offersList.forEach((eachEntry) => {
        if (eachEntry.id === value) {
          if (eachEntry?.nested_values && Object.keys(eachEntry?.nested_values)?.length) {
            setValuesForD1(Object.keys(eachEntry?.nested_values));
          }
          if (eachEntry?.nested_values === null) {
            setValuesForD1([]);
          }
        }
      });
    }
  };

  const getVendorPopulated = (offerFound) => {
    const selectedOffer = offers.find((offer) =>
      offer?.options?.some((option) => option.offer_id === offerFound.offer_id)
    );

    if (selectedOffer) {
      const vendor = vendorsListOptions.find(
        (eachVendor) => eachVendor.value === selectedOffer.vendor
      );

      setSelectVendor(vendor?.value);
      setSelectHeadline(selectedOffer.id);
    }
  };

  const updatedSelectedOptions = (value, offerId, _foundOffer) => {
    const selectedOffer = offersList.find((offer) => offer.id === value);
    const tempObject = {};
    const filteredOptions = selectedOffer?.options?.filter(
      (eachOption) => eachOption.offer_id === offerId
    )?.[0];

    const matchedOffer = offersList.find((offer) => offer.id === value);

    matchedOffer?.template_variables?.map((varValue, index) => {
      tempObject[varValue] = filteredOptions?.template_values?.[index]?.toString() || '';
      return tempObject[varValue];
    });
    setHeadlineOfferValues(tempObject);

    if (configureHeadline) {
      setVariableValues(tempObject);
      setPromotionData({
        ...promotionData,
        finance_offer: promotionData.finance_offer.map((offer, i) =>
          i === index
            ? {
                ...offer,
                custom_values: tempObject,
                headline_builder_id: value,
                ...(offerId !== undefined && offerId !== '' && { offer_id: offerId }),
              }
            : offer
        ),
      });
      return; // Early return to avoid the else block
    }

    // This was previously inside the else block
    const hasNoExpiryData = promoExpiry !== undefined && Object.keys(promoExpiry).length === 0;
    const hasExistingFinanceOffers = promotionData.finance_offer?.length > 0;

    let updatedFinanceOffer;
    let date;

    if (hasNoExpiryData) {
      date = formatDateToMDY(defaultExpiryDate);
      setShowDatePickerForFinanceOfferID(1);
      setExpiryDatesObj([{ type: 'expiresOn', id: 1, value: date }]);
      setPromoExpiry({ EXPIRY_DATE: date });

      const offerData = {
        headline_builder_id: value,
        offer_expiry: { expiry_dates: [date], offer_expiry_type: EXPIRY_DATE },
        ...(offerId !== undefined && offerId !== '' && { offer_id: offerId }),
      };

      updatedFinanceOffer = hasExistingFinanceOffers
        ? promotionData.finance_offer.map((offer, i) =>
            i === index ? { ...offer, ...offerData } : offer
          )
        : [offerData];
    } else {
      const offerData = {
        headline_builder_id: value,
        ...(offerId !== undefined && offerId !== '' && { offer_id: offerId }),
      };

      updatedFinanceOffer = hasExistingFinanceOffers
        ? promotionData.finance_offer.map((offer, i) =>
            i === index ? { ...offer, ...offerData } : offer
          )
        : [offerData];
    }

    setPromotionData({
      ...promotionData,
      finance_offer: updatedFinanceOffer,
    });
  };

  const debouncedFindOffer = (offerId) => {
    const { foundOffer, index: foundIndex } = findOfferById(offerId);

    if (foundOffer) {
      setOfferIdError(false);
      setModifiedOfferData(
        offers.filter((offer) => offer.vendor === offersList[foundIndex].vendor)
      );
      getVendorPopulated(foundOffer);
      handleHeadlineChange(offersList[foundIndex].id, offerId, foundOffer, true);
      updatedSelectedOptions(offersList[foundIndex].id, offerId, foundOffer);
    } else {
      setOfferIdError(true);
    }
  };

  const handleOfferInclude = (event) => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      setOfferApplicable(false);
      if (index === 1) {
        const financeOffers = [...promotionData.finance_offer];

        financeOffers.pop();
        setPromotionData({
          ...promotionData,
          finance_offer: financeOffers,
        });
      } else {
        const promotion = { ...promotionData };

        delete promotion.finance_offer;
        if (!promotion.discount_offer) {
          delete promotion.sweetener_offer;
        }
        setPromotionData(promotion);
        fetchHeadlineData(promotion);
      }
      setSelectHeadline('none');
      setPlanID('');
      setDisableConfig(true);
      setCustomHeadline('');
      setCustomSubHeadline('');
      setCustomDisclaimer('');
      setPromoExpiry({});
      setExpiryDatesObj({});
    } else {
      setOfferApplicable(true);
      setPromotionData({
        ...promotionData,
        finance_offer:
          promotionData.finance_offer?.length > 0
            ? promotionData.finance_offer.map((offer, i) => (i === index ? {} : offer))
            : [{}],
      });
    }
  };

  const handleConjuctionChange = (value) => {
    setSelectedConjunction(value);
    setPromotionData({
      ...promotionData,
      conjunction: value,
    });
  };

  const handleCustomHeadline = (value) => {
    setCustomHeadline(value);
    setPromotionData({
      ...promotionData,
      finance_offer: promotionData.finance_offer.map((offer, i) =>
        i === index ? { ...offer, headline: value } : offer
      ),
    });
  };

  const handleCustomSubHeadline = (value) => {
    setCustomSubHeadline(value);
    setPromotionData({
      ...promotionData,
      finance_offer: promotionData.finance_offer.map((offer, i) =>
        i === index ? { ...offer, sub_headline: value } : offer
      ),
    });
  };

  const handleCustomDisclaimer = (value) => {
    setCustomDisclaimer(value);
    setPromotionData({
      ...promotionData,
      finance_offer: promotionData.finance_offer.map((offer, i) =>
        i === index ? { ...offer, disclaimer: value } : offer
      ),
    });
  };

  const handleHeadlineChange = (value, offerId, foundOffer, fromDebounce = false) => {
    setSelectHeadline(value);
    setConfigureHeadline(false);

    if (!offerId) {
      setPlanID('');
      setOfferIdError(false);
      setHeadlineOfferValues({});
      setVariableValues({});
      delete promotionData?.finance_offer?.custom_values;
      delete promotionData?.finance_offer?.[index]?.offer_id;
    }

    // remove offer_id from finance_offer as it is taking the cached value of previous offer_id after changing the headline
    if (offerId) {
      delete promotionData?.finance_offer?.[index]?.offer_id;
    }

    if (!fromDebounce) {
      const item = offersList?.find((d) => d?.id === value);
      const istemplateValuesAvailableForHeadline =
        Array.isArray(item?.template_variables) && item?.template_variables?.length === 0;

      if (istemplateValuesAvailableForHeadline) {
        setPlanID(getOfferId(offersList, value));
        setOfferIdError(false);
      }

      if (promoExpiry !== undefined && Object.keys(promoExpiry).length === 0) {
        const date = formatDateToMDY(defaultExpiryDate);

        setShowDatePickerForFinanceOfferID(1);
        setExpiryDatesObj([{ type: 'expiresOn', id: 1, value: date }]);
        setPromoExpiry({ EXPIRY_DATE: date });
        setPromotionData({
          ...promotionData,
          finance_offer: promotionData.finance_offer.map((offer, i) =>
            i === index
              ? {
                  ...offer,
                  headline_builder_id: value,
                  custom: value === 'custom_offer',
                  offer_expiry: {
                    expiry_dates: [date],
                    offer_expiry_type: EXPIRY_DATE,
                  },
                  ...(offerId !== undefined &&
                    offerId !== '' &&
                    !istemplateValuesAvailableForHeadline && {
                      offer_id: offerId,
                    }),
                  ...(istemplateValuesAvailableForHeadline && {
                    offer_id: getOfferId(offersList, value),
                  }),
                }
              : offer
          ),
        });
      } else {
        setPromotionData({
          ...promotionData,
          finance_offer:
            promotionData.finance_offer?.length > 0
              ? promotionData.finance_offer.map((offer, i) =>
                  i === index
                    ? {
                        ...offer,
                        headline_builder_id: value,
                        headline: foundOffer?.template,
                        custom: value === 'custom_offer',
                        ...(offerId !== undefined &&
                          offerId !== '' &&
                          !istemplateValuesAvailableForHeadline && {
                            offer_id: offerId,
                          }),
                        ...(istemplateValuesAvailableForHeadline && {
                          offer_id: getOfferId(offersList, value),
                        }),
                      }
                    : offer
                )
              : [
                  {
                    headline_builder_id: value,
                    headline: foundOffer?.template,
                    custom: value === 'custom_offer',
                    ...(offerId !== undefined && offerId !== '' ? { offer_id: null } : {}),
                  },
                ],
        });
      }
    }

    if (
      !offersList.find((offer) => offer.id === value)?.template_variables?.length > 0 &&
      value !== 'custom_offer'
    ) {
      setDisableConfig(true);
    } else {
      setDisableConfig(false);
    }

    if (value !== 'none') {
      updateDropdownValues(value);
    }
  };

  const handlePlanIDChange = (value) => {
    const finance_offer = promotionData?.finance_offer?.[index];

    if (finance_offer?.custom) {
      const { foundOffer } = findOfferById(value);

      if (foundOffer) {
        promotionData.finance_offer[index].custom = false;
      }
    }
    setPlanID(value);
    if (value >= 0) {
      debouncedFindOffer(value);
    }
  };

  const handleVendorChange = (value) => {
    setSelectVendor(value);
    const selectedOffer = offersList.find((offer) => offer.vendor === value);

    if (value === 'all_vendor') {
      setModifiedOfferData(offers);
    } else {
      setModifiedOfferData(offers.filter((offer) => offer.vendor === value));
    }
    setPromotionData({
      ...promotionData,
      finance_offer: promotionData.finance_offer.map((offer, i) =>
        i === index ? { ...offer, vendor: value, headline_builder_id: selectedOffer?.id } : offer
      ),
    });
  };

  const filterDropdownValues = (selectedValue, part) => {
    const requiredHeadline = offersList.find((eachOffer) => eachOffer.id === selectHeadline);

    if (!requiredHeadline) {
      return;
    }

    const templateVariables = requiredHeadline.template_variables;
    const idx = templateVariables.indexOf(part);

    if (Object.keys(requiredHeadline?.nested_values || {})?.length) {
      if (idx === 0) {
        setValuesForD2(Object.keys(requiredHeadline?.nested_values[selectedValue]));
      } else if (idx === 1) {
        if (requiredHeadline?.nested_values[variableValues[templateVariables[idx - 1]]]) {
          setValuesForD3(
            requiredHeadline?.nested_values[variableValues[templateVariables[idx - 1]]][
              selectedValue
            ]
          );
        }
      }
    }
  };

  const handleAutocompleteChange = (value, part) => {
    if (value?.length) {
      const selectedOffer = offersList.find((eachOffer) => eachOffer.id === selectHeadline);
      const tempObj = { ...variableValues };

      tempObj[part] = value;
      setVariableValues(tempObj);
      setHeadlineOfferValues({ ...headlineOfferValues, ...tempObj });

      const selectedOption = selectedOffer?.options?.filter((option) => {
        const optionValues = Object.values(option.template_values);
        const tempObjValues = Object.values(tempObj);

        return (
          optionValues.length === tempObjValues.length &&
          optionValues.every((value, index) => value === tempObjValues[index])
        );
      });

      if (selectedOption?.length) {
        setPlanID(selectedOption[0]?.offer_id);
        setOfferIdError(false);
      }

      setSelectVendor(selectedOffer?.vendor);
      filterDropdownValues(value, part);

      setPromotionData({
        ...promotionData,
        finance_offer: promotionData.finance_offer.map((offer, i) =>
          i === index
            ? {
                ...offer,
                ...(selectedOption?.length && {
                  offer_id: selectedOption[0]?.offer_id,
                }),
                custom_values: tempObj,
              }
            : offer
        ),
      });
    }
  };

  const handleOfferExpiryDate = (data) => {
    if (data !== null) {
      const date = formatDateToMDY(data);

      if (promoExpiry[EXPIRY_DATE] === undefined || date !== promoExpiry[EXPIRY_DATE][0]) {
        setExpiryDatesObj([
          { type: 'expiresOn', id: showDatePickerForFinanceOfferID, value: date },
        ]);

        setPromotionData({
          ...promotionData,
          finance_offer:
            promotionData.finance_offer?.length > 0
              ? promotionData.finance_offer.map((offer, i) =>
                  i === index
                    ? {
                        ...offer,
                        offer_expiry: {
                          expiry_dates: [date],
                          offer_expiry_type: EXPIRY_DATE,
                        },
                      }
                    : offer
                )
              : [
                  {
                    offer_expiry: {
                      expiry_dates: [date],
                      offer_expiry_type: EXPIRY_DATE,
                    },
                  },
                ],
        });
        setPromoExpiry({ EXPIRY_DATE: date });
      }
    }
  };

  const handleOfferExpiryDateForInstallation = (data) => {
    const date = formatDateToMDY(data);

    if (
      promoExpiry[LAST_INSTALLATION_DATE] === undefined ||
      date !== promoExpiry[LAST_INSTALLATION_DATE][0]
    ) {
      setExpiryDatesObj([
        { type: 'scheduleBefore', id: showDatePickerForFinanceOfferID, value: data },
      ]);
      setPromotionData({
        ...promotionData,
        finance_offer:
          promotionData.finance_offer?.length > 0
            ? promotionData.finance_offer.map((offer, i) =>
                i === index
                  ? {
                      ...offer,
                      offer_expiry: {
                        expiry_dates: [date],
                        offer_expiry_type: LAST_INSTALLATION_DATE,
                      },
                    }
                  : offer
              )
            : [
                {
                  offer_expiry: {
                    expiry_dates: [date],
                    offer_expiry_type: LAST_INSTALLATION_DATE,
                  },
                },
              ],
      });
      setPromoExpiry({ LAST_INSTALLATION_DATE: date });
    }
  };

  const handleScheduleExpDate = (data) => {
    if (!initialHomeConsulSchedExpDateRef.current) {
      initialHomeConsulSchedExpDateRef.current = data;
    }
    const date = formatDateToMDY(data);
    let updatedDatesObj = Object.keys(expiryDatesObj).length > 0 ? expiryDatesObj : [];

    if (
      updatedDatesObj[0] !== undefined &&
      updatedDatesObj[0].offer_expiry_type !== LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE
    ) {
      updatedDatesObj = [
        {
          id: 31,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      ];
    } else {
      let found = false;

      updatedDatesObj.map((eachDate, idx) => {
        if (eachDate.id === 31) {
          updatedDatesObj[idx].value = date;
          found = true;
        }
      });
      if (!found) {
        updatedDatesObj = [
          ...updatedDatesObj,
          {
            id: 31,
            value: initialHomeConsulSchedExpDateRef?.current,
            offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
          },
        ];
      }
    }

    setExpiryDatesObj(updatedDatesObj);

    const dates = updatedDatesObj
      .sort((a, b) => a.id - b.id)
      .map((obj) => obj.value)
      ?.filter((value) => value !== null && value !== undefined);

    setPromotionData({
      ...promotionData,
      finance_offer:
        promotionData.finance_offer?.length > 0
          ? promotionData.finance_offer.map((offer, i) =>
              i === index
                ? {
                    ...offer,
                    offer_expiry: {
                      expiry_dates: dates,
                      offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
                    },
                  }
                : offer
            )
          : [
              {
                offer_expiry: {
                  expiry_dates: dates,
                  offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
                },
              },
            ],
    });

    setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: dates });
  };

  const handlePurchaseExpDate = (data) => {
    const date = formatDateToMDY(data);
    let updatedDatesObj = Object.keys(expiryDatesObj).length > 0 ? expiryDatesObj : [];

    if (
      updatedDatesObj[0] !== undefined &&
      updatedDatesObj[0].offer_expiry_type !== LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE
    ) {
      updatedDatesObj = [
        {
          id: 32,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      ];
    } else {
      let found = false;

      updatedDatesObj.map((eachDate, idx) => {
        if (eachDate.id === 32) {
          updatedDatesObj[idx].value = date;
          found = true;
        }
      });
      if (!found) {
        updatedDatesObj = [
          ...updatedDatesObj,
          {
            id: 32,
            value: date,
            offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
          },
        ];
      }
    }

    setExpiryDatesObj(updatedDatesObj);
    const dates = updatedDatesObj
      .sort((a, b) => a.id - b.id)
      .map((obj) => obj.value)
      ?.filter((value) => value !== null && value !== undefined);

    setPromotionData({
      ...promotionData,
      finance_offer: promotionData.finance_offer.map((offer, i) =>
        i === index
          ? {
              ...offer,
              offer_expiry: {
                expiry_dates: dates,
                offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
              },
            }
          : offer
      ),
    });

    setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: dates });
  };

  const handleConfigureHeadline = () => {
    if (configureHeadline === false) {
      setVariableValues(headlineOfferValues);
      setPromotionData({
        ...promotionData,
        finance_offer: promotionData.finance_offer.map((offer, i) =>
          i === index ? { ...offer, custom_values: headlineOfferValues } : offer
        ),
      });
    } else {
      setVariableValues({});
      delete promotionData?.finance_offer[index]?.custom_values;
      setPromotionData({
        ...promotionData,
      });
    }
    setConfigureHeadline(!configureHeadline);
  };

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const renderModifiedHeadline = (offersList, headline) => {
    const requiredHeadline = offersList.find((eachOffer) => eachOffer.id === headline);
    const template = requiredHeadline?.template;
    const templateVariables = requiredHeadline?.template_variables;

    if (templateVariables) {
      const pattern = new RegExp(`(${templateVariables.map(escapeRegExp).join('|')}|\\s)`);
      const parts = template.split(pattern);
      const modifiedParts = parts.map((part, idx) => {
        if (templateVariables.includes(part)) {
          const variableIndex = templateVariables.indexOf(part);

          // Extract the nested ternary for options into a separate variable
          let optionsArray = [];

          if (variableIndex === 0) {
            optionsArray = valuesForD1?.map((value) => value.toString());
          } else if (variableIndex === 1) {
            optionsArray = valuesForD2?.map((value) => value.toString());
          } else {
            optionsArray = valuesForD3?.map((value) => value.toString());
          }

          return {
            type: 'autocomplete',
            key: `dropdown-${part}-${idx}`,
            part,
            options: optionsArray,
            value: headlineOfferValues[part]?.toString() || '',
          };
        }
        return {
          type: 'text',
          key: `${part}_${idx}`,
          text: part,
        };
      });

      return modifiedParts;
    }
    return [];
  };

  return {
    // State values
    showDatePickerForFinanceOfferID,
    setShowDatePickerForFinanceOfferID,
    offerApplicable,
    configureHeadline,
    headlineOfferValues,
    selectVendor,
    selectHeadline,
    disableConfig,
    variableValues,
    customHeadline,
    customSubHeadline,
    customDisclaimer,
    planID,
    promoExpiry,
    offerIdError,
    modifiedOfferData,
    vendorsListOptions,

    // Handler functions
    handleOfferInclude,
    handleConjuctionChange,
    handleCustomHeadline,
    handleCustomSubHeadline,
    handleCustomDisclaimer,
    handleHeadlineChange,
    handlePlanIDChange,
    handleVendorChange,
    handleAutocompleteChange,
    handleOfferExpiryDate,
    handleOfferExpiryDateForInstallation,
    handleScheduleExpDate,
    handlePurchaseExpDate,
    handleConfigureHeadline,
    renderModifiedHeadline,

    // Utility function
    escapeRegExp,
  };
};
